import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SchoolApi } from '../../../api/client';
import { Keys } from '../../../api/hooks';
import type { StudentOverview } from '../../../api/types';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { getUserInfo } from '../../../../utils/remote';
import logoPng from '../../../../images/logo/logo.png';
import logoSvg from '../../../../images/logo/logo-icon.svg';
import {
  buildLetterFacts, buildStudentReportPdf, deriveWeekHighlights, studentReportFilename,
  type StudentReportData,
} from './studentReportPdf';

async function rasterise(src: string, size: number): Promise<string> {
  const img = new Image();
  img.src = src;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(img, 0, 0, size, size);
  return canvas.toDataURL('image/png');
}

/** The logo as a small PNG data URL: the 6000px PNG shrunk, or the SVG mark rasterised as a fallback. */
export async function loadLogoDataUrl(): Promise<string | undefined> {
  for (const src of [logoPng, logoSvg]) {
    try { return await rasterise(src, 256); } catch { /* try the next source */ }
  }
  return undefined;
}

const settle = <T,>(p: Promise<T>): Promise<T | null> => p.catch(() => null);

export function useStudentReportExport(id: number, overview: StudentOverview | undefined) {
  const qc = useQueryClient();
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function exportReport() {
    if (!overview || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const [academic, trend, mastery, commitments, engagement, parent, cycles, info, logoDataUrl] = await Promise.all([
        qc.fetchQuery({ queryKey: Keys.studentTab(id, 'academic'), queryFn: () => SchoolApi.studentAcademic(id) }),
        settle(qc.fetchQuery({ queryKey: Keys.studentWeeklyTrend(id, {}), queryFn: () => SchoolApi.studentWeeklyTrend(id, {}) })),
        settle(qc.fetchQuery({ queryKey: Keys.studentChapterMastery(id, { subject: 'all' }), queryFn: () => SchoolApi.studentChapterMastery(id, {}) })),
        settle(qc.fetchQuery({ queryKey: Keys.studentTab(id, 'commitments'), queryFn: () => SchoolApi.studentCommitments(id) })),
        settle(qc.fetchQuery({ queryKey: Keys.studentTab(id, 'engagement'), queryFn: () => SchoolApi.studentEngagement(id) })),
        settle(qc.fetchQuery({ queryKey: Keys.studentTab(id, 'parent'), queryFn: () => SchoolApi.studentParent(id) })),
        settle(qc.fetchQuery({ queryKey: Keys.studentCycles(id), queryFn: () => SchoolApi.studentCycles(id) })),
        settle(getUserInfo(token || undefined)),
        loadLogoDataUrl(),
      ]);
      const data: StudentReportData = {
        schoolName: (info?.institution_name as string) ?? '',
        generatedAt: new Date().toISOString(),
        overview, academic, trend, mastery, commitments, engagement, parent, cycles, logoDataUrl,
      };
      const highlights = deriveWeekHighlights(data);
      // The letter is written by the AI from the same facts the PDF shows.
      let letterError: unknown = null;
      const letter = await SchoolApi.studentParentLetter(id, buildLetterFacts(data, highlights)).catch((e) => { letterError = e; return null; });
      data.letter = letter?.letter ?? null;
      if (!data.letter) {
        // A 429 limit also opens the global dialog (the axios interceptor); an own-key error says why.
        const own = (letterError as { response?: { data?: { code?: string; detail?: string } } } | null)?.response?.data;
        const ownDetail = own?.code?.startsWith('openrouter_') ? own.detail : '';
        setNotice(ownDetail
          ? `${ownDetail} The report went out without the AI letter.`
          : 'The AI letter was not available, so the report went out without it.');
      }
      const doc = buildStudentReportPdf(data);
      doc.save(studentReportFilename(data, highlights.weekOf));
    } catch {
      setError('Could not build the report. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return { exportReport, busy, error, notice };
}
